import React from 'react';
import { formatCurrency } from '../../../../../../utils/helpers';
import './styles/payment-summary/PaymentSummary.css';

// Common Payment Breakdown Component
export const PaymentBreakdown = ({ appointment, hasPackage, getPackageDetails }) => {
  // Add debug logging
  console.log('Tax Settings:', {
    taxSettings: appointment.taxSettings,
    taxRate: appointment.taxSettings?.serviceRate,
    fullAppointment: appointment
  });

  const basePrice = (() => {
    if (appointment.paymentMethod === 'authorize') {
      if (hasPackage) {
        return appointment.priceBreakdown?.basePrice || getPackageDetails?.price || appointment.package?.price || 0;
      }
      return appointment.servicePrice || appointment.price || 0;
    }
    return hasPackage ? (getPackageDetails?.price || appointment.package?.price || 0) : (appointment.servicePrice || appointment.price || 0);
  })();

  const addonsTotal = (() => {
    // Don't calculate addons total if it's a package
    if (hasPackage) return 0;

    return (appointment.addons || appointment.selectedAddons || appointment.service?.selectedAddons || appointment.service?.addOns || [])
      .reduce((sum, addon) => sum + (parseFloat(addon.price) || 0), 0);
  })();

  const subtotal = basePrice + (hasPackage ? 0 : addonsTotal);
  const taxRate = appointment.taxSettings?.serviceRate || 0;
  const isEnabled = appointment.taxSettings?.isEnabled || false;
  
  // For Authorize, prefer stored amounts written at finalize time
  const totalTax = appointment.paymentMethod === 'authorize'
    ? (typeof appointment.taxAmount === 'number' ? appointment.taxAmount : (isEnabled ? (subtotal * (taxRate / 100)) : 0))
    : (isEnabled ? (subtotal * (taxRate / 100)) : 0);
  const totalPrice = appointment.paymentMethod === 'authorize'
    ? (appointment.totalPrice || (subtotal + totalTax))
    : (subtotal + totalTax);

  // Get tip amount if it exists
  const tipAmount = appointment.balancePayment?.tip?.amount || 0;
  const finalTotal = totalPrice + tipAmount;

  // Debug logging
  console.log('Payment Summary Calculation:', {
    hasPackage,
    basePrice,
    addonsTotal,
    subtotal,
    taxRate,
    totalTax,
    tipAmount,
    finalTotal,
    calculation: `${basePrice} ${!hasPackage ? `+ ${addonsTotal}` : ''} = ${subtotal} + ${totalTax} (tax) + ${tipAmount} (tip) = ${finalTotal}`
  });

  return (
    <>
      <div className="payment-summary-breakdown">
        <div className="payment-summary-row payment-summary-row-divider">
          <p className="payment-summary-label">{hasPackage ? 'Package Subtotal' : 'Subtotal'}</p>
          <p className="payment-summary-value">{formatCurrency(subtotal)}</p>
        </div>

      {isEnabled && totalTax > 0 && (
        <>
          <p className="payment-summary-mini-heading">Tax Breakdown ({taxRate.toFixed(2)}%)</p>
          <div className="payment-summary-row payment-summary-row-sub">
            <p className="payment-summary-sub-label">{hasPackage ? 'Package Tax' : 'Service Tax'}</p>
            <p className="payment-summary-sub-value">{formatCurrency(totalTax)}</p>
          </div>
        </>
      )}

      {(() => {
        const balanceTip = appointment.balancePayment?.tip;
        const hasTip = balanceTip?.amount > 0;

        if (hasTip) {
          return (
            <div className="payment-summary-row payment-summary-row-dashed">
              <p className="payment-summary-sub-label">Tip ({balanceTip.percentage}%)</p>
              <p className="payment-summary-sub-value">{formatCurrency(balanceTip.amount)}</p>
            </div>
          );
        }
        return null;
      })()}

        <div className="payment-summary-row payment-summary-row-total">
          <p className="payment-summary-total-label">Total</p>
          <p className="payment-summary-total-value">{formatCurrency(finalTotal)}</p>
        </div>
      </div>
    </>
  );
};

const PaymentMethodSummary = ({ appointment, hasPackage, getPackageDetails }) => {
  // Get package data from all possible sources
  const packageData = appointment.package || appointment.selectedPackage || appointment.packageDetails || null;

  // Get the correct addons based on whether it's a package or service
  const addons = hasPackage 
    ? (packageData?.selectedAddons || []) 
    : (
        appointment.addons 
        || appointment.selectedAddons 
        || appointment.service?.selectedAddons 
        || appointment.service?.addOns 
        || []
      );

  return (
    <div className="payment-summary-card">
      <div className="payment-summary-card-head">
        <h3 className="payment-summary-card-title">{hasPackage ? 'Package Details' : 'Service Details'}</h3>
      </div>

      <div className="payment-summary-row">
        <p className="payment-summary-label">
          {hasPackage ? (
            packageData?.name || packageData?.searchName || 'Package'
          ) : (
            appointment.serviceName
          )}
        </p>
        <p className="payment-summary-value">
          {hasPackage ? 
            formatCurrency(packageData?.currentPrice || packageData?.price || 0) : 
            formatCurrency(Number(parseFloat(appointment.servicePrice || appointment.price || 0).toFixed(2)))
          }
        </p>
      </div>

      {hasPackage && packageData?.basePrice && packageData?.currentPrice && packageData?.basePrice !== packageData?.currentPrice && (
        <div className="payment-summary-discount-row">
          <p className="payment-summary-original-price">
            Original: {formatCurrency(packageData.basePrice)}
          </p>
          <p className="payment-summary-discount-pill">
            ({packageData.discount}% off)
          </p>
        </div>
      )}

      {addons?.map((addon, index) => (
        <div key={index} className="payment-summary-row payment-summary-addon-row">
          <p className="payment-summary-sub-label">{addon.name}</p>
          <p className="payment-summary-sub-value">{hasPackage ? 'Included' : formatCurrency(addon.price)}</p>
        </div>
      ))}

      <PaymentBreakdown 
        appointment={appointment} 
        hasPackage={hasPackage} 
        getPackageDetails={getPackageDetails}
      />
    </div>
  );
};

// Main Payment Summary Component
const PaymentSummary = ({ appointment, hasPackage, getPackageDetails }) => {
  return (
    <section className="payment-summary-section">
      <header className="payment-summary-header">
        <h2 className="payment-summary-title">Payment Summary</h2>
        <p className="payment-summary-subtitle">Full price breakdown for this appointment</p>
      </header>

      <PaymentMethodSummary
        appointment={appointment}
        hasPackage={hasPackage}
        getPackageDetails={getPackageDetails}
      />
    </section>
  );
};

export default PaymentSummary; 